/**
 * Creates an error view for displaying to the user (probably in a modal dialog).
 * Can supply an optional message and stackTrace.  For example:
 *
 * new ErrorView(xhr.responseJSON)
 * new ErrorView({ message: 'Something bad happened', stackTrace: '' })
 */

import Backbone from 'backbone'

import errorViewMessage from './error-view-message.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import ServerErrorDetailsDialog from './server-error-details-dialog'
import CollapsibleStackTrace from 'scoreboard/components/collapsible-stack-trace'
import errorStackTraceDialog from './error-stack-trace-dialog.hbs'
import appDisplay from 'scoreboard/components/app-display'
import config from './config'

var ErrorView = Backbone.View.extend({

	className: 'error-view',

	events: {
		'click .additional-details': 'stackTraceDialog'
	},

	/**
	 * @param {String} [options.messageTitle] MR.p code for initial dialog.
	 *
	 * @param {String} [options.title='common.error.server'] MR.p code for the error dialog title.
	 *
	 * @param {String} [options.messageCode] MP.p code to be translated and used as the message
	 *
	 * @param {String|Array} [options.arguments] arguments to be used for options.messageCode
	 *
	 * @param {String} [options.message] An escaped message, can contain markup
	 *
	 * @param {String} [options.stackTrace]
	 *
	 * @param {Boolean} [options.bypassMessageView] Apply if there is a messageCode but the view should go directly to the stacktrace dialog
	 */
	initialize: function(options) {
		this.options = options || {}
	},

	render: function() {
		const {message, messageCode, bypassMessageView} = this.options
		// Issue #24324
		// Show the stacktrace instantly if there is a message but we are
		// bypassing the message only view. (i.e. When clicking on a banner error)
		if (bypassMessageView) {
			this.$el.empty().append(errorStackTraceDialog({
				title: i18n.get('common.error.technicalDetails'),
				banner: message
			}))
			// #30354 Don't show the stack trace box if there isn't a stack trace
			if (this.options.stackTrace) {
				this.collapsibleStackTrace = new CollapsibleStackTrace({
					header: i18n.get('common.details'),
					stackTrace: this.options.stackTrace
				})
				this.$('.stack-trace').append(this.collapsibleStackTrace.el)
				this.collapsibleStackTrace.render()
			}
			this.$('.dialog-body').addClass('server-error-details')
		} else if ((messageCode || message) && messageCode !== 'common.error.jsp.title') {
			// If there is a message to show, we will only show the message and not the
			// stackTrace. There will be text-link within the message that will open
			// a new dialog that displays the stackTrace
			this.$el
				.addClass('dialog-message')
				.empty()
				.append(errorViewMessage(this.context()))
		} else {
			// If there is no message, will instantly show the stackTrace in the dialog
			this.$el.empty().append(errorStackTraceDialog({
				title: i18n.get(_.get(this.options, 'title', 'common.error.server')),
				banner: message
			}))
			// #48723 don't show the stack trace if the config is set to hide them
			if (this.options.stackTrace && !config.hideStackTraces) {
				this.collapsibleStackTrace = new CollapsibleStackTrace({
					header: i18n.get('common.error.stackTrace'),
					stackTrace: this.options.stackTrace
				})
				this.$('.stack-trace').append(this.collapsibleStackTrace.el)
				this.collapsibleStackTrace.render()
			}
		}
		return this
	},

	context: function() {
		var message = this.options.message || this.options.messageCode
		if (message === 'common.error.jsp.title') {
			message = i18n.get('common.error.generic')
		}
		const translated = i18n.highlight(message, this.options.arguments)
		return _.extend({}, this.options, {message: translated})
	},

	// If there was a message to display, the stackTrace wasn't included in the
	// error-view dialog. Instead, there is an "Additional Details" link that will
	// open a separate dialog to show the stackTrace.
	stackTraceDialog: function() {
		// ES5 throws 'arguments is a reserved word in strict mode'
		const {message, messageCode, stackTrace, arguments: args} = this.options
		appDisplay.dialog(
			new ServerErrorDetailsDialog({
				title: i18n.get('common.error.technicalDetails'),
				banner: message || i18n.get(messageCode, args),
				stackTrace
			})
		)
	},

	remove: function() {
		Backbone.View.prototype.render.apply(this, this.arguments)
		_.invokeMap([this.collapsibleStackTrace], 'remove')
	}

})

export default ErrorView

ErrorView.__filename__ = 'components/error-view.js'
